import { ArrowDown, ArrowUp, Pencil, Plus, Sparkles } from 'lucide-react'
import { useMemo, useState } from 'react'

import { useKindMutations } from '@/api/vocab'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { WithTooltip } from '@/components/ui/tooltip'
import { cn } from '@/components/ui/utils'
import type { Domain, KindDef } from '@/schema'
import { bareId, digitKeys, labelOf, useKinds } from '@/stores/vocabStore'

import { KindEditorDialog, summariseSemantics } from './KindEditorDialog'

/**
 * One domain of the vocabulary as a table.
 *
 * Row order **is** the shortcut order: a kind's own digit key wins, then the
 * enabled rows fill `1–9` in order (the annotator's rule; a person's pins
 * only change their own browser), so the arrows that move a row are not decoration.
 * Kinds are never deleted (§4.4.4 rule 2) — a kind you no longer want is
 * deprecated with an alias, and readers resolve it, so old documents keep
 * rendering.
 */

export interface VocabularyTableProps {
  domain: Domain
  /** Show disabled and deprecated rows too. */
  showAll?: boolean
  className?: string
}

export function VocabularyTable({ domain, showAll = false, className }: VocabularyTableProps) {
  const rows = useKinds(domain)
  const { patch } = useKindMutations()
  const [filter, setFilter] = useState('')
  const [editing, setEditing] = useState<{ kind: KindDef | null } | null>(null)

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase()
    return rows
      .filter((k) => (showAll ? true : k.enabled && !k.deprecated))
      .filter((k) => !needle || k.id.toLowerCase().includes(needle) || labelOf(k).toLowerCase().includes(needle))
  }, [rows, showAll, filter])

  /** Swap two rows' `sort_order`; the server bumps `rev` for each write. */
  const move = (index: number, delta: number) => {
    const a = visible[index]
    const b = visible[index + delta]
    if (!a || !b) return
    patch.mutate({ domain: a.domain, id: a.id, body: { sort_order: b.sort_order } })
    patch.mutate({ domain: b.domain, id: b.id, body: { sort_order: a.sort_order } })
  }

  // The same rule the annotator uses for its number keys (`digitKeys`), plus letter keys for other lists.
  const keyOf = useMemo(() => {
    const m = new Map<string, string>()
    for (const { key, kind } of digitKeys(rows)) m.set(kind.id, key)
    for (const k of rows) if (k.enabled && !k.deprecated && k.shortcut && !/^[0-9]$/.test(k.shortcut) && !m.has(k.id)) m.set(k.id, k.shortcut)
    return m
  }, [rows])
  const shortcutFor = (kind: KindDef): string | null => keyOf.get(kind.id) ?? null

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by name or id"
          aria-label={`Filter ${domain}`}
          className="max-w-xs"
          type="search"
        />
        <span className="text-xs text-ink-3">
          {visible.length} of {rows.length}
        </span>
        <Button variant="primary" size="sm" className="ml-auto" onClick={() => setEditing({ kind: null })}>
          <Plus aria-hidden />
          Add
        </Button>
      </div>

      {visible.length === 0 ? (
        <p className="card px-4 py-10 text-center text-sm text-ink-2">Nothing in this list yet.</p>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <caption className="sr-only">Kinds in the {domain} domain</caption>
            <thead>
              <tr className="border-b border-line text-left text-xs text-ink-3">
                <th scope="col" className="w-10 px-3 py-2 font-medium">
                  Key
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Name
                </th>
                <th scope="col" className="hidden px-3 py-2 font-medium md:table-cell">
                  What it does
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Where
                </th>
                <th scope="col" className="w-28 px-3 py-2 text-right font-medium">
                  Order
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((kind, i) => {
                const key = shortcutFor(kind)
                return (
                  <tr key={kind.id} className={cn('border-b border-line last:border-b-0 hover:bg-paper-2', !kind.enabled && 'opacity-60')}>
                    <td className="px-3 py-2">
                      {key ? <kbd className="rounded border border-line bg-paper-2 px-1.5 py-0.5 font-mono text-xs text-ink-2">{key}</kbd> : null}
                    </td>
                    <th scope="row" className="px-3 py-2 text-left font-normal">
                      <div className="flex items-center gap-2">
                        <span className="size-3 shrink-0 rounded-full border border-line" style={{ background: kind.color }} aria-hidden />
                        <button
                          type="button"
                          className="truncate font-medium text-ink hover:underline"
                          onClick={() => setEditing({ kind })}
                          aria-label={`Edit ${labelOf(kind)}`}
                        >
                          {labelOf(kind)}
                        </button>
                        <span className="truncate font-mono text-xs text-ink-3" title={kind.id}>
                          {bareId(kind.id)}
                        </span>
                        {kind.origin === 'user' ? <Badge tone="accent">yours</Badge> : null}
                        {kind.origin === 'ai' ? (
                          <Badge tone="info" className="gap-1 [&_svg]:size-3">
                            <Sparkles aria-hidden />
                            from Claude
                          </Badge>
                        ) : null}
                        {kind.deprecated ? <Badge tone="warn">retired{kind.alias_of ? ` → ${bareId(kind.alias_of)}` : ''}</Badge> : null}
                        {!kind.enabled ? <Badge tone="neutral">off</Badge> : null}
                      </div>
                      {kind.description ? <p className="mt-0.5 truncate text-xs text-ink-3">{kind.description}</p> : null}
                    </th>
                    <td className="hidden max-w-xs px-3 py-2 md:table-cell">
                      <span className="line-clamp-2 text-xs text-ink-2">{summariseSemantics(kind)}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-3">{kind.applies_to.join(', ')}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-0.5">
                        <WithTooltip label="Move up — the first nine get keys 1–9">
                          <Button variant="ghost" size="sm" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move ${labelOf(kind)} up`}>
                            <ArrowUp aria-hidden />
                          </Button>
                        </WithTooltip>
                        <Button variant="ghost" size="sm" disabled={i === visible.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${labelOf(kind)} down`}>
                          <ArrowDown aria-hidden />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditing({ kind })} aria-label={`Edit ${labelOf(kind)}`}>
                          <Pencil aria-hidden />
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing ? <KindEditorDialog open onOpenChange={(o) => !o && setEditing(null)} domain={domain} kind={editing.kind} /> : null}
    </div>
  )
}
